import { useState, useEffect, FormEvent } from 'react'
import { GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'
import { SearchIcon } from '@chakra-ui/icons'
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Heading,
  Text,
  Stack,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react'
import { useBlogCms } from '@/hooks/useBlogCms'
import { Article } from '@/features/education/article'
import { Loading } from '@/components/Loading'

const SearchPage = () => {
  const router = useRouter()
  const t = useTranslations()
  const { compassContent, getContent } = useBlogCms()
  const [query, setQuery] = useState('')
  const [loader, setLoader] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [lastSearchTerm, setLastSearchTerm] = useState('')

  const performSearch = async (searchTerm: string) => {
    const trimmed = searchTerm.trim()
    if (!trimmed) return

    setLastSearchTerm(trimmed)
    setHasSearched(true)
    setLoader(true)
    const filterString = `title:~'${trimmed}'`

    router.push(
      { pathname: router.pathname, query: { filter: trimmed } },
      undefined,
      { shallow: true }
    )

    await getContent({
      path: '/content/posts',
      params: `&limit=100&order=published_at%20desc&filter=${encodeURIComponent(
        filterString
      )}`,
    })

    setLoader(false)
  }

  useEffect(() => {
    const { filter } = router.query
    if (typeof filter === 'string') {
      setQuery(filter)
      performSearch(filter)
    }
  }, [router.query.filter])

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    performSearch(query)
  }

  const posts = compassContent?.posts || []
  const hasResults = !loader && posts.length > 0

  return (
    <Box
      bg={useColorModeValue('lightModeSecondaryBG', 'darkModeSecondaryBG')}
      pt={5}
    >
      <Box
        maxW="1200px"
        mx="auto"
        p={4}
        display="flex"
        flexDirection="column"
        alignItems={loader ? 'center' : undefined}
        minHeight="90vh"
      >
        <Box as="form" onSubmit={handleSearch} mb={6} width="100%">
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={2}
            align="center"
          >
            <InputGroup flex="1">
              <InputLeftElement cursor="pointer" onClick={handleSearch}>
                <SearchIcon />
              </InputLeftElement>
              <Input
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                _placeholder={{ color: 'gray.500' }}
              />
            </InputGroup>
          </Stack>
        </Box>

        {loader ? (
          <Loading />
        ) : hasResults ? (
          <>
            <Heading as="h2" size="md" mb={4}>
              {`${posts.length} ${t('common.results')}${
                posts.length !== 1 && 's'
              }`}
            </Heading>
            <Flex flexWrap="wrap" paddingTop="40px">
              {compassContent?.posts?.map((article: any) => {
                return <Article key={article?.id} article={article} />
              })}
            </Flex>
          </>
        ) : (
          hasSearched && (
            <Text mt={4} color="gray.500">
              {`${t('common.no-results-found')} "${lastSearchTerm}".`}
            </Text>
          )
        )}
      </Box>
    </Box>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../../../locales/${locale}.json`)).default,
      htmlClass: 'education',
      seo: {
        title: 'Search – Compass Mining Content',
        breadcrumbTitle: 'Search – Compass Mining Content',
      },
    },
  }
}

export default SearchPage
